import { viewpointBuilderNavigationOperations } from 'viewpointBuilder/viewpointBuilderNavigation/viewpointBuilderNavigationOperations';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  tap,
  withLatestFrom,
  Observable,
} from 'rxjs';
import { initialSearchComponentViewState } from 'viewpointBuilder/selectContextTab/toViewState';
import {
  dispatchAction,
  extractPayload,
  ofType,
  routine,
  spreadTap,
  withNamespace,
} from '@ardoq/rxbeach';
import { getViewpointGroupingRulesCommands } from 'viewpointBuilder/addGroupingRulesTab/viewpointGroupingRulesCommands';
import { getViewpointBuilderFormattingCommands } from 'viewpointBuilder/formattingTabs/viewpointBuilderFormattingCommands';
import { isEqual, sumBy } from 'lodash';
import {
  requestFetchInstanceCounts,
  updateTraversalStartContext,
} from 'viewpointBuilder/traversals/editTraversalActions';
import {
  ComponentSearchViewState,
  ViewpointBuilderAdvancedSearchState,
  ViewpointBuilderAdvancedSearchValidState,
} from 'viewpointBuilder/selectContextTab/types';
import { prototypeInterface } from 'modelInterface/prototype/prototypeInterface';
import {
  setIsLoading,
  setLoadedComponents,
} from 'viewpointBuilder/selectContextTab/actions';
import { QueryBuilderQuery } from '@ardoq/api-types';
import {
  TraversalBuilderViewState,
  ViewpointBuilderContext,
} from 'viewpointBuilder/types';
import {
  collapsedPathTabUnselected,
  selectTab,
  setCollapsedPathsCount,
  setFormattingRulesCount,
  setGraphCountsAndComponentTypeRepresentation,
  setGroupingRulesCount,
  setRequiredComponentsCount,
  setSavedViewpointDetails,
  setSelectedComponentTypeAndCount,
} from 'viewpointBuilder/viewpointBuilderNavigation/actions';
import { ComponentTypeProps } from '../components/ComponentType';
import { ViewpointFormattingProps } from '../formattingTabs/viewpointFormattingTypes';
import { GroupingRulesState } from '../addGroupingRulesTab/groupingRulesTypes';
import { ViewpointBuilderNavigationViewModel } from '../viewpointBuilderNavigation/types';
import { viewpointGroupingRulesOperations } from '../addGroupingRulesTab/viewpointGroupingRulesOperations';
import { getFormattingRulesCount } from '../formattingTabs/getFormattingRulesCount';
import { getRepresentationDataWithColorVariants } from 'viewpointBuilder/components/getRepresentationDataWithColorVariants';
import { MetaInfoState } from '../viewpointMetaInfo/metaInfoTypes';
import { viewpointBuilderTracking } from '../viewpointBuilderTracking';
import { isSavedPathCollapsingRule } from '../collapsePathsTab/types';
import { alwaysSelectAll$ } from 'componentHierarchies/advancedSearchSelectAllConfig';

export const getUpdatePerspectivesOptionsRoutine = (
  context: ViewpointBuilderContext,
  editTraversals$: Observable<TraversalBuilderViewState>,
  namespace: string
) => {
  if (
    viewpointBuilderNavigationOperations.getModeFromContext(context) ===
    'advanced'
  ) {
    const groupingsCommands = getViewpointGroupingRulesCommands(namespace);
    const formattingCommands = getViewpointBuilderFormattingCommands(namespace);
    return editTraversals$.pipe(
      map(({ perspectivesOptionsArgs }) => perspectivesOptionsArgs),
      distinctUntilChanged(isEqual),
      tap(args => {
        groupingsCommands.getGroupingOptions(args);
        formattingCommands.getFormattingOptions(args);
      })
    );
  }
  return { subscribe: () => ({ unsubscribe: () => null }) };
};

export const getNavigationTabDetailsOnSelectedComponentsRoutine = (
  componentSearch$: Observable<ComponentSearchViewState>,
  namespace: string
) =>
  componentSearch$.pipe(
    map(
      ({
        selectedStartComponentTypeNames,
        canSelectOnlyComponentType,
        componentType,
        selectedComponentsCount,
      }) => {
        return {
          selectedComponentTypes: canSelectOnlyComponentType
            ? [componentType].filter(Boolean)
            : selectedStartComponentTypeNames,
          selectedComponentCount: selectedComponentsCount,
        };
      }
    ),
    distinctUntilChanged((a, b) => isEqual(a, b)),
    tap(payload =>
      dispatchAction(setSelectedComponentTypeAndCount(payload), namespace)
    )
  );

export const getNavigationTabDetailsOnMetaInfoRoutine = (
  metaInfo$: Observable<MetaInfoState>,
  namespace: string
) =>
  metaInfo$.pipe(
    map(({ name }) => {
      return {
        viewpointName: name,
      };
    }),
    distinctUntilChanged((a, b) => isEqual(a, b)),
    tap(payload => dispatchAction(setSavedViewpointDetails(payload), namespace))
  );

export const getNavigationTabDetailsOnGraphRoutine = (
  editTraversals$: Observable<TraversalBuilderViewState>,
  namespace: string
) =>
  editTraversals$.pipe(
    map(
      ({ filters, graphEdges, graphNodes, startComponentType, startNode }) => {
        return {
          filtersCount: sumBy(
            Object.values(filters),
            filtersPerNode => filtersPerNode.filters.length
          ),
          edgesCount: graphEdges.size,
          nodesCount: graphNodes.size,
          selectedComponentTypeRepresentation:
            getComponentTypeRepresentationForStartContext(
              startComponentType,
              startNode
            ),
        };
      }
    ),
    distinctUntilChanged((a, b) => isEqual(a, b)),
    tap(payload =>
      dispatchAction(
        setGraphCountsAndComponentTypeRepresentation(payload),
        namespace
      )
    )
  );

const getComponentTypeRepresentationForStartContext = (
  startComponentType: TraversalBuilderViewState['startComponentType'],
  startNode: TraversalBuilderViewState['startNode']
): ComponentTypeProps | null => {
  if (!startComponentType || !startNode?.getRepresentationData()) {
    return null;
  }

  const representationData = startNode!.getRepresentationData()!;

  return {
    label: startComponentType,
    representationData:
      getRepresentationDataWithColorVariants(representationData),
  };
};

export const getNavigationTabDetailsOnFormattingRoutine = (
  viewpointFormatting$: Observable<ViewpointFormattingProps>,
  viewpointBuilderNavigation$: Observable<ViewpointBuilderNavigationViewModel>,
  namespace: string
) =>
  viewpointFormatting$.pipe(
    map(getFormattingRulesCount),
    withLatestFrom(viewpointBuilderNavigation$),
    tap(
      ([
        count,
        { labelFormattingRulesCount, conditionalFormattingRulesCount },
      ]) => {
        if (
          !isEqual(count, {
            labelFormattingRulesCount,
            conditionalFormattingRulesCount,
          })
        ) {
          dispatchAction(setFormattingRulesCount(count), namespace);
        }
      }
    )
  );

export const getNavigationTabDetailsOnGroupingRoutine = (
  viewpointGroupingRules$: Observable<GroupingRulesState>,
  namespace: string
) =>
  viewpointGroupingRules$.pipe(
    map(state => {
      return {
        groupingRulesCount:
          viewpointGroupingRulesOperations.getGroupingRulesCount(state),
      };
    }),
    distinctUntilChanged((a, b) => isEqual(a, b)),
    tap(count => dispatchAction(setGroupingRulesCount(count), namespace))
  );

export const getUpdateInstanceCountsRoutine = (
  editTraversals$: Observable<TraversalBuilderViewState>,
  namespace: string
) =>
  editTraversals$.pipe(
    map(
      ({
        stateAsSavableAttributes,
        traversalStartQuery,
        traversalStartSet,
        shouldIncludeInstanceCounts,
        instanceCountsVersion,
      }) => {
        return {
          ...stateAsSavableAttributes,
          startSet: traversalStartSet,
          startQuery: traversalStartQuery,
          shouldIncludeInstanceCounts,
          instanceCountsVersion,
        };
      }
    ),
    distinctUntilChanged((a, b) => isEqual(a, b)),
    // To account for user interactions.
    debounceTime(300),
    tap(
      ({
        startQuery,
        startSet,
        paths,
        filters,
        startSetFilterId,
        pathMatching,
        startSetExpansion,
        shouldIncludeInstanceCounts,
      }) => {
        if (!shouldIncludeInstanceCounts) {
          return;
        }

        if ((startSet && startSet.length > 0) || startQuery) {
          dispatchAction(
            requestFetchInstanceCounts({
              startSet,
              startQuery,
              paths,
              filters,
              startSetFilterId,
              pathMatching,
              startSetExpansion,
            }),
            namespace
          );
        }
      }
    )
  );

/**
 * The search state stream defines the start set and type for the traversal.
 * This subscription listens to changes in the search state and updates the
 * traversal start context.
 * The search state is persistent, so it will get reset to the initial state.
 * We need to ignore that iteration, it could screw up the traversal, e.g.
 * clear the traversal on editing a traversal block.
 */
export const getUpdateTraversalStartContextRoutine = (
  componentSearch$: Observable<ComponentSearchViewState>,
  namespace: string
) =>
  componentSearch$.pipe(
    filter(state => !isEqual(state, initialSearchComponentViewState)),
    map(
      ({
        selectedComponentIds,
        startQuery,
        isApplyingTraversalDisabled,
        componentType,
        canSelectOnlyComponentType,
        selectedStartComponentTypeNames,
      }) => {
        const startComponentType =
          selectedStartComponentTypeNames[0] ?? componentType;

        if (canSelectOnlyComponentType && startComponentType) {
          return {
            traversalStartSet: [],
            traversalStartQuery: null,
            startComponentType,
            selectedStartComponentTypeNames,
          };
        }

        if (isApplyingTraversalDisabled || !startComponentType)
          return {
            traversalStartSet: null,
            traversalStartQuery: null,
            startComponentType: null,
            selectedStartComponentTypeNames,
          };

        return {
          traversalStartSet: startQuery ? [] : selectedComponentIds,
          traversalStartQuery: startQuery ?? null,
          startComponentType,
          selectedStartComponentTypeNames,
        };
      }
    ),
    distinctUntilChanged((a, b) => isEqual(a, b)),
    tap(state => dispatchAction(updateTraversalStartContext(state), namespace))
  );

const isValidAdvancedSearch = (
  state: ViewpointBuilderAdvancedSearchState
): state is ViewpointBuilderAdvancedSearchValidState =>
  state?.status === 'valid' && state.query !== null;

export const getUpdateAdvancedSearchQueryRoutine = (
  componentSearch$: Observable<ComponentSearchViewState>,
  namespace: string
) =>
  componentSearch$.pipe(
    map(({ advancedSearchState }) => advancedSearchState),
    distinctUntilChanged((a, b) => isEqual(a, b)),
    filter(isValidAdvancedSearch),
    map(({ query }) => query),
    debounceTime(300),
    withLatestFrom(alwaysSelectAll$),
    tap(async ([query, alwaysSelectAll]: [QueryBuilderQuery, boolean]) => {
      dispatchAction(setIsLoading(true), namespace);

      const { results, totalCount } =
        await prototypeInterface.loadComponentsFromQueryBuilder(query);

      dispatchAction(
        setLoadedComponents({
          loadedComponents: results,
          totalCount,
          selectAll: alwaysSelectAll,
        }),
        namespace
      );
      dispatchAction(setIsLoading(false), namespace);
    })
  );

export const getNavigationTabDetailsOnCollapsedPaths = (
  editTraversals$: Observable<TraversalBuilderViewState>,
  namespace: string
) =>
  editTraversals$.pipe(
    map(({ pathCollapsingViewState: { rules } }) => rules),
    distinctUntilChanged((a, b) => isEqual(a, b)),
    tap(rules => {
      const collapsedPathsCount = rules.filter(
        isSavedPathCollapsingRule
      ).length;
      dispatchAction(
        setCollapsedPathsCount({ collapsedPathsCount }),
        namespace
      );
    })
  );

export const getNavigationTabDetailsOnRequiredComponents = (
  editTraversals$: Observable<TraversalBuilderViewState>,
  namespace: string
) =>
  editTraversals$.pipe(
    map(({ requiredNodeIds }) => requiredNodeIds),
    distinctUntilChanged((a, b) => isEqual(a, b)),
    tap(requiredNodeIds => {
      const requiredComponentsCount = requiredNodeIds.length;
      dispatchAction(
        setRequiredComponentsCount({ requiredComponentsCount }),
        namespace
      );
    })
  );

export const getNavigationTabDetailsOnCollapsedPathsUnselect = (
  viewpointBuilderNavigation$: Observable<ViewpointBuilderNavigationViewModel>,
  namespace: string
) =>
  viewpointBuilderNavigation$.pipe(
    map(({ selectedTab }) => selectedTab),
    pairwise(),
    tap(([prevTab, currentTab]) => {
      if (
        prevTab === 'COLLAPSE_PATHS_TAB' &&
        currentTab !== 'COLLAPSE_PATHS_TAB'
      ) {
        dispatchAction(collapsedPathTabUnselected(), namespace);
      }
    })
  );

export const getTrackSelectTabRoutine = (
  metaInfo$: Observable<MetaInfoState>,
  namespace: string
) =>
  routine(
    withNamespace(namespace),
    ofType(selectTab),
    extractPayload(),
    map(({ tab }) => tab),
    withLatestFrom(metaInfo$),
    spreadTap(viewpointBuilderTracking.trackClickNavigationTab)
  );
